import React, { useEffect, useRef, useState } from 'react';
import * as signalR from "@microsoft/signalr";
import axios from "axios";
import './Meters.css';
import click from './sounds/shortclick.mp3';
import groundFloor from './images/FuTipMap_720px.png';

const FLOOR_WIDTH = 730;
const FALL_SENSOR_TYPE = 24;

export default function FallsensorsTest() {
    const [dev, setDev] = useState([]);
    const [displaydev, setDisplsyDev] = useState([]);
    const [retry, setRetry] = useState(false);
    const [hoveredDeviceId, setHoveredDeviceId] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const isInitialLoad = useRef(true);
    const [pins_g, setPins_g] = useState([]);
    const connection = useRef(null);

    const playClickSound = () => {
        const audio = new Audio(click);
        audio.play();
    };

    const handleCellEnter = (deviceId) => {
        setHoveredDeviceId(deviceId);
        playClickSound();
    };

    const handleCellLeave = () => {
        setHoveredDeviceId(null);
    };

    // API calls for test actions
    const handleTrigger = async (deviceEui) => {
        try {
            await axios.post(`/api/meters/test-fall/${deviceEui}`, { action: "trigger" });
            console.log("Triggered fall for", deviceEui);
        } catch (err) {
            console.error("Trigger failed", err);
        }
    };

    const handleDisarm = async (deviceEui) => {
        try {
            await axios.post(`/api/meters/test-fall/${deviceEui}`, { action: "disarm" });
            console.log("Disarmed fall for", deviceEui);
        } catch (err) {
            console.error("Disarm failed", err);
        }
    };

    // Floorplan and Pin components (same as Fallsensors.js)
    const FloorplanImage = ({ src, alt }) => (
        <div className="estate-image">
            <img src={src} alt={alt} width={FLOOR_WIDTH} />
        </div>
    );

    const PinIcon = ({ id, x, y, isHovered, type }) => (
        <div style={{
            position: "absolute",
            left: `${x}px`,
            top: `${y}px`,
            transform: "translate(-50%, -50%)",
            zIndex: 5,
            cursor: "pointer"
        }}
            onMouseEnter={() => handleCellEnter(id)}
            onMouseLeave={handleCellLeave}
        >
            {(isHovered || type) && (
                <div style={{
                    position: "absolute",
                    width: "24px",
                    height: "24px",
                    borderRadius: "50%",
                    backgroundColor: "rgba(255, 215, 0, 0.3)",
                    border: "2px solid rgba(255, 215, 0, 0.8)",
                    animation: "pulse 1.5s infinite",
                    transform: "translate(-50%, -50%)",
                }} />
            )}
            <div style={{
                position: "absolute",
                width: "12px",
                height: "12px",
                borderRadius: "50%",
                backgroundColor: isHovered ? "yellow" : "red",
                transform: "translate(-50%, -50%)",
            }} />
        </div>
    );

    const ImageWithPins = ({ pins, hoveredDeviceId }) => (
        <div>
            {pins.map(pin => (
                <PinIcon
                    key={pin.id}
                    id={pin.id}
                    x={pin.x}
                    y={pin.y}
                    isHovered={hoveredDeviceId === pin.id}
                    type={pin.type === 0 ? false : true}
                />
            ))}
        </div>
    );

    // DeviceCell with long-press detection
    const DeviceCell = ({ device, isHovered, onTrigger, onDisarm }) => {
        const pressTimer = useRef(null);
        const isLongPress = useRef(false);

        const handleMouseDown = () => {
            isLongPress.current = false;
            pressTimer.current = setTimeout(() => {
                isLongPress.current = true;
                onDisarm(device.deviceEui);
            }, 1000);
        };

        const handleMouseUp = () => {
            if (pressTimer.current) {
                clearTimeout(pressTimer.current);
            }
            if (!isLongPress.current) {
                onTrigger(device.deviceEui);
            }
            isLongPress.current = false;
        };

        const cellStyle = {
            fontSize: "16px",
            overflow: "hidden",
            color: "#f7f4ef",
            backgroundColor: isHovered ? "#1a237e" : "#525252",
            transition: "all 0.3s ease",
            borderRadius: "4px",
            padding: "8px",
            margin: "4px",
            boxShadow: isHovered
                ? "0 15px 30px rgba(0,0,0,0.6), 0 10px 10px rgba(0,0,0,0.4)"
                : "0 8px 16px rgba(0,0,0,0.4), 0 4px 8px rgba(0,0,0,0.3)",
            transform: isHovered ? "translateY(-10px) scale(1.05)" : "translateY(-3px) scale(1.01)",
            zIndex: isHovered ? 20 : 5,
            position: "relative",
            cursor: "pointer"
        };

        return (
            <td
                key={device.id}
                style={cellStyle}
                onMouseDown={handleMouseDown}
                onMouseUp={handleMouseUp}
                onTouchStart={handleMouseDown}
                onTouchEnd={handleMouseUp}
                onMouseEnter={() => handleCellEnter(device.id)}
                onMouseLeave={handleCellLeave}
            >
                <table>
                    <tbody>
                        <tr style={{ fontSize: "24px", fontWeight: "bold", height: "78px" }}>{device.label}</tr>
                        <tr>
                            <div style={{
                                fontSize: "24px",
                                overflow: "hidden",
                                fontWeight: "bold",
                                color: isHovered ? "gold" : (device.status === "異常" ? "#ff5252" : "#4caf50")
                            }}>
                                <div style={{
                                    marginTop: "auto",
                                    textAlign: "center",
                                    fontWeight: "bold",
                                    color: "#f5f0e9",
                                    fontSize: "26px",
                                    backgroundColor: device.status === "異常" ? "#d32f2f" : "#1976d2",
                                    borderRadius: "4px",
                                    padding: "4px",
                                    boxShadow: isHovered ? "0 0 8px rgba(255,215,0,0.7)" : "none"
                                }}>
                                    {device.status}
                                </div>
                            </div>
                        </tr>
                    </tbody>
                </table>
            </td>
        );
    };

    const DeviceList = () => {
        return (
            <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '4px' }}>
                <tbody>
                    <tr>
                        <td colSpan="2" style={{ verticalAlign: 'top' }}>
                            <div style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: '4px',
                                backgroundColor: '#888888',
                                borderRadius: '12px',
                                padding: '4px',
                                margin: '4px 0'
                            }}>
                                <h3 style={{
                                    width: '100%',
                                    padding: '8px',
                                    margin: 0,
                                    color: '#ffffff',
                                    fontSize: '28px',
                                    fontWeight: '600',
                                    backgroundColor: '#ff7f27',
                                    borderRadius: '8px'
                                }}>跌倒檢測 (測試模式 - 點擊觸發 / 長按復位)</h3>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                    {displaydev.map(device => (
                                        <DeviceCell
                                            key={device.id}
                                            device={device}
                                            isHovered={hoveredDeviceId === device.id}
                                            onTrigger={handleTrigger}
                                            onDisarm={handleDisarm}
                                        />
                                    ))}
                                </div>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        );
    };

    // Data fetching and SignalR (same as Fallsensors.js)
    useEffect(() => {
        const httpClient = axios.create({
            baseURL: 'https://futipiot.ddns.net',
            headers: { 'Content-Type': 'application/json' }
        });

        const getData = async () => {
            try {
                const response = await httpClient.get('/api/meters');
                const deviceDetails = response.data.map(item => ({
                    id: item.deviceId,
                    deviceEui: item.deviceEui,
                    label: item.label,
                    status: item.status,
                    mapx: item.mapx === null ? null : item.mapx * FLOOR_WIDTH / 720,
                    mapy: item.mapy === null ? null : item.mapy * FLOOR_WIDTH / 720,
                    type: item.type,
                    data_type: item.dataType,
                    active: item.active
                }));
                const fallSensors = deviceDetails.filter(d => d.data_type === FALL_SENSOR_TYPE && d.active === 1);
                const pins = fallSensors.filter(d => d.mapx !== null).map(d => ({ ...d, x: d.mapx, y: d.mapy }));
                setPins_g(pins);
                setDev(deviceDetails);
                setDisplsyDev(fallSensors);
                setIsLoading(false);
            } catch (error) {
                console.error(error);
                setRetry(true);
            }
        };

        getData();
    }, [retry]);

    useEffect(() => {
        const httpClient = axios.create({
            baseURL: 'https://futipiot.ddns.net',
            headers: { 'Content-Type': 'application/json' }
        });

        const getData = async () => {
            try {
                const response = await httpClient.get('/api/meters');
                const deviceDetails = response.data.map(item => ({
                    id: item.deviceId,
                    deviceEui: item.deviceEui,
                    label: item.label,
                    status: item.status,
                    mapx: item.mapx === null ? null : item.mapx * FLOOR_WIDTH / 720,
                    mapy: item.mapy === null ? null : item.mapy * FLOOR_WIDTH / 720,
                    type: item.type,
                    data_type: item.dataType,
                    active: item.active
                }));
                const fallSensors = deviceDetails.filter(d => d.data_type === FALL_SENSOR_TYPE && d.active === 1);
                const pins = fallSensors.filter(d => d.mapx !== null).map(d => ({ ...d, x: d.mapx, y: d.mapy }));
                setPins_g(pins);
                setDev(deviceDetails);
                setDisplsyDev(fallSensors);
                if (isInitialLoad.current) isInitialLoad.current = false;
            } catch (error) {
                console.error(error);
            }
        };

        const conn = new signalR.HubConnectionBuilder().withUrl("/devicehub").build();
        connection.current = conn;
        conn.start().catch(err => console.error(err));
        conn.on("ReceiveDeviceData", () => getData());
        conn.on("ReceiveFallSensorUpdate", () => getData());

        return () => {
            conn.stop();
        };
    }, []);

    return (
        <div className="containerstyle">
            <table>
                <tbody>
                    <tr>
                        <td style={{ verticalAlign: "top", width: "760px" }}>
                            <div className="floorpin">
                                {!isLoading && (
                                    <>
                                        <FloorplanImage src={groundFloor} alt="Ground Floor" />
                                        <ImageWithPins pins={pins_g} hoveredDeviceId={hoveredDeviceId} />
                                    </>
                                )}
                            </div>
                        </td>
                        <td style={{ verticalAlign: "top" }}>
                            <table style={{ fontSize: "12px", verticalAlign: "top", marginTop: "8px", width: "1000px" }}>
                                <tbody>
                                    <DeviceList />
                                </tbody>
                            </table>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
}